<template>
    <Modal :show="show" title="Take Photo" size="lg" @close="handleClose">
        <div class="space-y-4">
            <!-- Camera Access Error -->
            <div v-if="error" class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div class="flex">
                    <svg class="w-5 h-5 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                        <h3 class="text-sm font-medium text-red-800 dark:text-red-200">Camera Access Error</h3>
                        <p class="text-sm text-red-700 dark:text-red-300 mt-1">{{ error }}</p>
                    </div>
                </div>
            </div>

            <!-- Camera View -->
            <div v-if="!capturedImage" class="relative bg-black rounded-lg overflow-hidden">
                <video
                    ref="videoElement"
                    autoplay
                    playsinline
                    class="w-full h-auto max-h-[60vh] object-contain"
                    :class="{ 'opacity-0': !cameraReady }"
                ></video>

                <!-- Loading Overlay -->
                <div v-if="!cameraReady && !error" class="absolute inset-0 flex items-center justify-center bg-gray-900">
                    <div class="text-center">
                        <svg class="animate-spin h-12 w-12 text-white mx-auto mb-3" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <p class="text-white text-sm">Initializing camera...</p>
                    </div>
                </div>

                <!-- Camera Controls Overlay -->
                <div v-if="cameraReady && !error" class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
                    <button
                        @click="capturePhoto"
                        type="button"
                        class="mx-auto block w-16 h-16 bg-white rounded-full border-4 border-gray-300 hover:border-blue-500 transition-colors shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                        <span class="sr-only">Take Photo</span>
                    </button>
                </div>
            </div>

            <!-- Preview Captured Image -->
            <div v-else class="relative bg-black rounded-lg overflow-hidden">
                <img
                    :src="capturedImage"
                    alt="Captured photo"
                    class="w-full h-auto max-h-[60vh] object-contain"
                />
            </div>

            <!-- Hidden canvas for image capture -->
            <canvas ref="canvasElement" class="hidden"></canvas>
        </div>

        <template #footer="{ close }">
            <div class="flex justify-between items-center w-full">
                <!-- Cancel/Close Button -->
                <button
                    @click="handleClose"
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    {{ capturedImage ? 'Cancel' : 'Close' }}
                </button>

                <!-- Action Buttons (only show when image is captured) -->
                <div v-if="capturedImage" class="flex space-x-3">
                    <button
                        @click="retakePhoto"
                        type="button"
                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Retake
                    </button>
                    <button
                        @click="confirmPhoto"
                        type="button"
                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Use Photo
                    </button>
                </div>
            </div>
        </template>
    </Modal>
</template>

<script setup>
import { ref, watch, onUnmounted } from 'vue';
import Modal from '@/Components/Common/Modal.vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close', 'captured']);

const videoElement = ref(null);
const canvasElement = ref(null);
const stream = ref(null);
const capturedImage = ref(null);
const cameraReady = ref(false);
const error = ref(null);

// Initialize camera when modal opens
watch(() => props.show, (newValue) => {
    if (newValue) {
        initCamera();
    } else {
        cleanup();
    }
});

const initCamera = async () => {
    error.value = null;
    cameraReady.value = false;
    capturedImage.value = null;

    try {
        // Request camera access with preference for rear camera
        const constraints = {
            video: {
                facingMode: 'environment', // Prefer rear camera on mobile
                width: { ideal: 1920 },
                height: { ideal: 1080 },
            },
            audio: false,
        };

        stream.value = await navigator.mediaDevices.getUserMedia(constraints);

        // Wait for video element to be available
        await new Promise(resolve => setTimeout(resolve, 100));

        if (videoElement.value) {
            videoElement.value.srcObject = stream.value;

            // Wait for video to be ready
            videoElement.value.onloadedmetadata = () => {
                cameraReady.value = true;
            };
        }
    } catch (err) {
        console.error('Camera access error:', err);

        if (err.name === 'NotAllowedError') {
            error.value = 'Camera access was denied. Please allow camera access in your browser settings and try again.';
        } else if (err.name === 'NotFoundError') {
            error.value = 'No camera found on this device.';
        } else if (err.name === 'NotReadableError') {
            error.value = 'Camera is already in use by another application.';
        } else {
            error.value = `Failed to access camera: ${err.message}`;
        }
    }
};

const capturePhoto = () => {
    if (!videoElement.value || !canvasElement.value) return;

    const video = videoElement.value;
    const canvas = canvasElement.value;

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to data URL
    capturedImage.value = canvas.toDataURL('image/jpeg', 0.9);
};

const retakePhoto = () => {
    capturedImage.value = null;
    cameraReady.value = true;
};

const confirmPhoto = () => {
    if (!capturedImage.value) return;

    // Convert data URL to Blob
    fetch(capturedImage.value)
        .then(res => res.blob())
        .then(blob => {
            // Create a File object from the Blob
            const timestamp = new Date().getTime();
            const file = new File([blob], `photo_${timestamp}.jpg`, { type: 'image/jpeg' });

            // Emit the captured photo
            emit('captured', file);

            // Close the modal
            handleClose();
        })
        .catch(err => {
            console.error('Failed to convert image:', err);
            error.value = 'Failed to process the captured photo. Please try again.';
        });
};

const handleClose = () => {
    cleanup();
    emit('close');
};

const cleanup = () => {
    // Stop all camera streams
    if (stream.value) {
        stream.value.getTracks().forEach(track => track.stop());
        stream.value = null;
    }

    // Clear video element
    if (videoElement.value) {
        videoElement.value.srcObject = null;
    }

    // Reset state
    cameraReady.value = false;
    capturedImage.value = null;
    error.value = null;
};

// Cleanup on component unmount
onUnmounted(() => {
    cleanup();
});
</script>
